import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { PlusIcon, TrashIcon, SearchIcon, MapPinIcon, BookOpenIcon, XIcon, InfoIcon } from 'lucide-react';
import { getDefaultCurrency } from '../../utils/appSettings';
import { StandardizedNotes, NoteVisibility } from '../common/StandardizedNotes';
export function PurchaseOrderForm({
  initialData = null,
  onSave,
  isConversion = false,
  locale = 'en-US'
}) {
  const [formData, setFormData] = useState({
    name: '',
    vendor: '',
    vendorId: '',
    items: [{
      description: '',
      quantity: 1,
      unitPrice: 0,
      type: 'Item',
      uom: 'EA',
      taxCode: 'STD',
      taxRate: 10,
      taxAmount: 0,
      catalogItemId: '' // ID of catalog item if selected from catalog
    }],
    notes: '',
    noteVisibility: 'Internal' as NoteVisibility,
    noteVisibilityDetails: '',
    deliveryDate: '',
    paymentTerms: '30',
    shipmentMethod: '',
    currency: getDefaultCurrency().code,
    deliveryAddress: {
      type: 'entity',
      addressId: '',
      customAddress: {
        name: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      }
    },
    deliveryContact: {
      name: '',
      phone: '',
      email: ''
    }
  });
  // Add errors state
  const [errors, setErrors] = useState({
    lineItems: []
  });
  // Sample vendors for the dropdown
  const [vendors, setVendors] = useState([{
    id: 'V001',
    name: 'Office Depot',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V002',
    name: 'Dell Technologies',
    country: 'United States',
    hasCatalog: true
  }, {
    id: 'V003',
    name: 'PrintWorks Inc.',
    country: 'Canada',
    hasCatalog: false
  }, {
    id: 'V004',
    name: 'Server Solutions Inc.',
    country: 'United Kingdom',
    hasCatalog: true
  }, {
    id: 'V005',
    name: 'Global Supplies Co.',
    country: 'Australia',
    hasCatalog: false
  }]);
  // Sample catalog items for vendors
  const [vendorCatalogs, setVendorCatalogs] = useState({
    V001: [{
      id: 'CAT001-001',
      name: 'Premium Paper - A4',
      description: 'Premium quality A4 paper, 500 sheets',
      type: 'Item',
      uom: 'EA',
      unitPrice: 12.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-002',
      name: 'Ballpoint Pens - Blue',
      description: 'Pack of 10 blue ballpoint pens',
      type: 'Item',
      uom: 'EA',
      unitPrice: 5.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-003',
      name: 'Office Chair - Ergonomic',
      description: 'Ergonomic office chair with lumbar support',
      type: 'Item',
      uom: 'EA',
      unitPrice: 249.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT001-004',
      name: 'Desk Organizer',
      description: 'Desktop organizer with multiple compartments',
      type: 'Item',
      uom: 'EA',
      unitPrice: 19.99,
      taxCode: 'STD',
      taxRate: 10
    }],
    V002: [{
      id: 'CAT002-001',
      name: 'Laptop - Business Series',
      description: 'Business laptop with 16GB RAM, 512GB SSD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1299.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-002',
      name: 'Desktop Computer - Standard',
      description: 'Standard desktop computer for office use',
      type: 'Item',
      uom: 'EA',
      unitPrice: 899.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-003',
      name: 'Monitor - 24 inch',
      description: '24-inch LED monitor, Full HD',
      type: 'Item',
      uom: 'EA',
      unitPrice: 199.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT002-004',
      name: 'IT Support - Basic',
      description: 'Basic IT support package, 10 hours',
      type: 'Service',
      uom: 'HR',
      unitPrice: 75.0,
      taxCode: 'STD',
      taxRate: 10
    }],
    V004: [{
      id: 'CAT004-001',
      name: 'Server - Entry Level',
      description: 'Entry level server for small businesses',
      type: 'Item',
      uom: 'EA',
      unitPrice: 1499.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-002',
      name: 'Server - Enterprise',
      description: 'Enterprise grade server with high performance',
      type: 'Item',
      uom: 'EA',
      unitPrice: 3999.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-003',
      name: 'Network Switch - 24 Port',
      description: '24-port managed network switch',
      type: 'Item',
      uom: 'EA',
      unitPrice: 349.99,
      taxCode: 'STD',
      taxRate: 10
    }, {
      id: 'CAT004-004',
      name: 'Server Maintenance',
      description: 'Annual server maintenance package',
      type: 'Service',
      uom: 'YEAR',
      unitPrice: 999.99,
      taxCode: 'STD',
      taxRate: 10
    }]
  });
  // Sample addresses for different contexts
  const [addresses, setAddresses] = useState({
    tenant: [{
      id: 'TA001',
      name: 'Corporate HQ',
      street: '123 Main St',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
      country: 'United States'
    }, {
      id: 'TA002',
      name: 'Distribution Center',
      street: '456 Warehouse Blvd',
      city: 'Chicago',
      state: 'IL',
      postalCode: '60007',
      country: 'United States'
    }],
    entity: [{
      id: 'EA001',
      name: 'North America Office',
      street: '789 Business Park',
      city: 'Boston',
      state: 'MA',
      postalCode: '02108',
      country: 'United States'
    }, {
      id: 'EA002',
      name: 'European Office',
      street: '10 Tech Square',
      city: 'London',
      postalCode: 'EC1A 1BB',
      country: 'United Kingdom'
    }],
    site: [{
      id: 'SA001',
      name: 'Manufacturing Plant',
      street: '567 Industrial Way',
      city: 'Detroit',
      state: 'MI',
      postalCode: '48127',
      country: 'United States'
    }, {
      id: 'SA002',
      name: 'Research Lab',
      street: '890 Innovation Dr',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94107',
      country: 'United States'
    }]
  });
  const [showCustomAddress, setShowCustomAddress] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [filteredVendors, setFilteredVendors] = useState(vendors);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [currentLineItemIndex, setCurrentLineItemIndex] = useState(0);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [filteredCatalogItems, setFilteredCatalogItems] = useState([]);
  // Generate a unique Purchase Order Number
  const generatePONumber = () => {
    const currentYear = new Date().getFullYear();
    // Generate a random 4-digit number (in a real app, this would be sequential)
    const randomNum = Math.floor(1000 + Math.random() * 9000);
    return `PO-${currentYear}-${randomNum}`;
  };
  // Unit of measure options
  const uomOptions = ['EA', 'KG', 'LB', 'M', 'CM', 'L', 'ML', 'HR', 'DAY', 'MONTH', 'YEAR'];
  // Tax codes with descriptions and rates
  const taxCodes = [{
    code: 'STD',
    name: 'Standard Rate',
    rate: 10
  }, {
    code: 'RED',
    name: 'Reduced Rate',
    rate: 5
  }, {
    code: 'ZERO',
    name: 'Zero Rate',
    rate: 0
  }, {
    code: 'EXM',
    name: 'Exempt',
    rate: 0
  }];
  // Function to check if an item description suggests office supplies
  const isOfficeSupplies = description => {
    const officeSupplyTerms = ['pen', 'pencil', 'paper', 'notebook', 'stapler', 'clip', 'folder', 'binder'];
    return officeSupplyTerms.some(term => description.toLowerCase().includes(term));
  };
  // Function to get GL code suggestion based on item description
  const getGLCodeSuggestion = item => {
    if (isOfficeSupplies(item.description)) {
      return {
        code: '6100.100',
        name: 'Expenses, Office Supplies'
      };
    }
    return null;
  };
  // Function to apply the suggested GL code
  const applySuggestedGLCode = (index, glCode) => {
    handleItemChange(index, 'glCode', glCode);
  };
  useEffect(() => {
    if (initialData) {
      // If we have initial data (for conversion from requisition)
      setFormData({
        name: initialData.name || '',
        vendor: initialData.vendor || '',
        vendorId: initialData.vendorId || '',
        items: initialData.items && initialData.items.length > 0 ? initialData.items.map(item => ({
          ...item,
          type: item.type || 'Item',
          uom: item.uom || 'EA',
          taxCode: item.taxCode || 'STD',
          taxRate: item.taxRate || 10,
          taxAmount: item.taxAmount || 0,
          catalogItemId: item.catalogItemId || ''
        })) : [{
          description: '',
          quantity: 1,
          unitPrice: 0,
          type: 'Item',
          uom: 'EA',
          taxCode: 'STD',
          taxRate: 10,
          taxAmount: 0,
          catalogItemId: ''
        }],
        notes: initialData.notes || '',
        noteVisibility: initialData.noteVisibility || 'Internal',
        noteVisibilityDetails: initialData.noteVisibilityDetails || '',
        deliveryDate: initialData.deliveryDate || '',
        paymentTerms: initialData.paymentTerms || '30',
        shipmentMethod: initialData.shipmentMethod || '',
        currency: initialData.currency || getDefaultCurrency().code,
        deliveryAddress: initialData.deliveryAddress || {
          type: 'entity',
          addressId: '',
          customAddress: {
            name: '',
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
          }
        },
        deliveryContact: initialData.deliveryContact || {
          name: '',
          phone: '',
          email: ''
        }
      });
    } else {
      // For new POs, generate a PO number automatically
      setFormData(prevData => ({
        ...prevData,
        name: generatePONumber()
      }));
    }
  }, [initialData]);
  useEffect(() => {
    // Filter vendors based on search query
    if (vendorSearchQuery) {
      const filtered = vendors.filter(vendor => vendor.name.toLowerCase().includes(vendorSearchQuery.toLowerCase()) || vendor.id.toLowerCase().includes(vendorSearchQuery.toLowerCase()));
      setFilteredVendors(filtered);
    } else {
      setFilteredVendors(vendors);
    }
  }, [vendorSearchQuery, vendors]);
  useEffect(() => {
    // Filter catalog items based on search query
    if (showCatalogModal && formData.vendorId) {
      const catalog = vendorCatalogs[formData.vendorId] || [];
      if (catalogSearchQuery) {
        const filtered = catalog.filter(item => item.name.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.description.toLowerCase().includes(catalogSearchQuery.toLowerCase()) || item.id.toLowerCase().includes(catalogSearchQuery.toLowerCase()));
        setFilteredCatalogItems(filtered);
      } else {
        setFilteredCatalogItems(catalog);
      }
    }
  }, [catalogSearchQuery, formData.vendorId, showCatalogModal, vendorCatalogs]);
  const handleChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    if (field === 'taxCode') {
      // Find the tax rate for the selected tax code
      const selectedTaxCode = taxCodes.find(tax => tax.code === value);
      const taxRate = selectedTaxCode ? selectedTaxCode.rate : 0;
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        taxRate: taxRate
      };
      // Recalculate tax amount
      const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
      updatedItems[index].taxAmount = subtotal * taxRate / 100;
    } else {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value
      };
      // Recalculate tax amount if quantity or unit price changes
      if (field === 'quantity' || field === 'unitPrice') {
        const subtotal = updatedItems[index].quantity * updatedItems[index].unitPrice;
        updatedItems[index].taxAmount = subtotal * updatedItems[index].taxRate / 100;
      }
    }
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        description: '',
        quantity: 1,
        unitPrice: 0,
        type: 'Item',
        uom: 'EA',
        taxCode: 'STD',
        taxRate: 10,
        taxAmount: 0,
        catalogItemId: ''
      }]
    });
  };
  const removeItem = index => {
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    setFormData({
      ...formData,
      items: updatedItems
    });
  };
  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };
  const calculateTaxTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.taxAmount, 0);
  };
  const calculateTotal = () => {
    return calculateSubtotal() + calculateTaxTotal();
  };
  const handleVendorSelect = vendor => {
    setFormData({
      ...formData,
      vendor: vendor.name,
      vendorId: vendor.id
    });
    setVendorSearchQuery('');
  };
  const handleAddressTypeChange = e => {
    const addressType = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        type: addressType,
        addressId: ''
      }
    });
    setShowCustomAddress(addressType === 'custom');
  };
  const handleAddressSelect = e => {
    const addressId = e.target.value;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        addressId: addressId
      }
    });
  };
  const handleCustomAddressChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      deliveryAddress: {
        ...formData.deliveryAddress,
        customAddress: {
          ...formData.deliveryAddress.customAddress,
          [name.replace('customAddress_', '')]: value
        }
      }
    });
  };
  const handleDeliveryContactChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      deliveryContact: {
        ...formData.deliveryContact,
        [name.replace('deliveryContact_', '')]: value
      }
    });
  };
  const openCatalogModal = index => {
    setCurrentLineItemIndex(index);
    setCatalogSearchQuery('');
    // Reset filtered catalog items to show all items for the vendor
    if (formData.vendorId && vendorCatalogs[formData.vendorId]) {
      setFilteredCatalogItems(vendorCatalogs[formData.vendorId]);
    } else {
      setFilteredCatalogItems([]);
    }
    setShowCatalogModal(true);
  };
  const closeCatalogModal = () => {
    setShowCatalogModal(false);
    setCatalogSearchQuery('');
  };
  const handleCatalogItemSelect = catalogItem => {
    const updatedItems = [...formData.items];
    // Calculate tax amount
    const taxAmount = catalogItem.unitPrice * catalogItem.taxRate / 100;
    // Update the line item with catalog item details
    updatedItems[currentLineItemIndex] = {
      description: catalogItem.description,
      quantity: 1,
      unitPrice: catalogItem.unitPrice,
      type: catalogItem.type,
      uom: catalogItem.uom,
      taxCode: catalogItem.taxCode,
      taxRate: catalogItem.taxRate,
      taxAmount: taxAmount,
      catalogItemId: catalogItem.id,
      itemCode: catalogItem.id // Set the itemCode to the catalog item's ID
    };
    setFormData({
      ...formData,
      items: updatedItems
    });
    closeCatalogModal();
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Validate line items
    const lineItemErrors = formData.items.map(item => {
      const itemErrors = {};
      if (!item.description) {
        itemErrors.description = 'Description is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        itemErrors.quantity = 'Quantity must be greater than 0';
      }
      return Object.keys(itemErrors).length > 0 ? itemErrors : null;
    });
    // Update errors state
    setErrors({
      lineItems: lineItemErrors
    });
    // Only save if there are no errors
    if (!lineItemErrors.some(error => error !== null)) {
      onSave({
        ...formData,
        total: calculateTotal()
      }, initialData);
    }
  };
  // Check if the selected vendor has a catalog
  const selectedVendorHasCatalog = () => {
    if (!formData.vendorId) return false;
    const vendor = vendors.find(v => v.id === formData.vendorId);
    return vendor ? vendor.hasCatalog : false;
  };
  return <form onSubmit={handleSubmit} data-id="element-14058">
      <div className="shadow sm:rounded-md sm:overflow-hidden" data-id="element-14059">
        <div className="px-4 py-5 bg-white space-y-6 sm:p-6" data-id="element-14060">
          <div className="grid grid-cols-6 gap-6" data-id="element-14061">
            <div className="col-span-6 sm:col-span-3" data-id="element-14062">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-14063">
                Purchase Order Number
              </label>
              <div className="mt-1 flex rounded-md shadow-sm" data-id="element-14064">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleChange} className="focus:ring-indigo-500 focus:border-indigo-500 flex-1 block w-full rounded-md sm:text-sm border-gray-300 bg-gray-50" readOnly={!initialData} data-id="element-14065" />
              </div>
              {!initialData && <p className="mt-1 text-xs text-gray-500" data-id="element-14066">
                  Automatically generated purchase order number
                </p>}
            </div>
            {/* Vendor Lookup */}
            <div className="col-span-6 sm:col-span-3" data-id="element-14067">
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700" data-id="element-14068">
                Vendor
              </label>
              <div className="mt-1 relative" data-id="element-14069">
                <div className="flex" data-id="element-14070">
                  <div className="relative flex-grow" data-id="element-14071">
                    <input type="text" id="vendorSearch" value={vendorSearchQuery} onChange={e => setVendorSearchQuery(e.target.value)} placeholder="Search vendors..." className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14072" />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-14073">
                      <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-14074" />
                    </div>
                  </div>
                </div>
                {/* Selected vendor display */}
                {formData.vendor && <div className="mt-2 flex items-center p-2 bg-gray-50 border border-gray-200 rounded-md" data-id="element-14075">
                    <span className="text-sm font-medium text-gray-700" data-id="element-14076">
                      {formData.vendor}
                    </span>
                    <span className="ml-2 text-xs text-gray-500" data-id="element-14077">
                      ({formData.vendorId})
                    </span>
                    {selectedVendorHasCatalog() && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-14078">
                        <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-14079" />
                        Catalog Available
                      </span>}
                  </div>}
                {/* Vendor search results */}
                {vendorSearchQuery && <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md overflow-hidden" data-id="element-14080">
                    <ul className="max-h-60 overflow-auto" data-id="element-14081">
                      {filteredVendors.length > 0 ? filteredVendors.map(vendor => <li key={vendor.id} className="cursor-pointer p-2 hover:bg-gray-50" onClick={() => handleVendorSelect(vendor)} data-id="element-14082">
                            <div className="flex justify-between" data-id="element-14083">
                              <span className="font-medium" data-id="element-14084">{vendor.name}</span>
                              <span className="text-xs text-gray-500" data-id="element-14085">
                                {vendor.id}
                              </span>
                            </div>
                            <div className="flex items-center" data-id="element-14086">
                              <span className="text-xs text-gray-500" data-id="element-14087">
                                {vendor.country}
                              </span>
                              {vendor.hasCatalog && <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-14088">
                                  <BookOpenIcon className="h-3 w-3 mr-1" data-id="element-14089" />
                                  Catalog
                                </span>}
                            </div>
                          </li>) : <li className="p-2 text-center text-sm text-gray-500" data-id="element-14090">
                          No vendors found
                        </li>}
                    </ul>
                  </div>}
              </div>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-14091">
              <label htmlFor="deliveryDate" className="block text-sm font-medium text-gray-700" data-id="element-14092">
                Expected Delivery Date
              </label>
              <input type="date" name="deliveryDate" id="deliveryDate" value={formData.deliveryDate} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14093" />
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-14094">
              <label htmlFor="paymentTerms" className="block text-sm font-medium text-gray-700" data-id="element-14095">
                Payment Terms
              </label>
              <select id="paymentTerms" name="paymentTerms" value={formData.paymentTerms} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-14096">
                <option value="15" data-id="element-14097">Net 15</option>
                <option value="30" data-id="element-14098">Net 30</option>
                <option value="45" data-id="element-14099">Net 45</option>
                <option value="60" data-id="element-14100">Net 60</option>
                <option value="immediate" data-id="element-14101">Immediate Payment</option>
              </select>
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-14102">
              <label htmlFor="shipmentMethod" className="block text-sm font-medium text-gray-700" data-id="element-14103">
                Shipment Method
              </label>
              <input type="text" name="shipmentMethod" id="shipmentMethod" value={formData.shipmentMethod} onChange={handleChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14104" />
            </div>
            <div className="col-span-6 sm:col-span-3" data-id="element-14105">
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-14106">
                Currency
              </label>
              <select id="currency" name="currency" value={formData.currency} onChange={handleChange} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-14107">
                <option value="AUD" data-id="element-14108">AUD - Australian Dollar</option>
                <option value="USD" data-id="element-14109">USD - US Dollar</option>
                <option value="EUR" data-id="element-14110">EUR - Euro</option>
                <option value="GBP" data-id="element-14111">GBP - British Pound</option>
                <option value="CAD" data-id="element-14112">CAD - Canadian Dollar</option>
                <option value="JPY" data-id="element-14113">JPY - Japanese Yen</option>
              </select>
            </div>
            {/* Delivery Address Section */}
            <div className="col-span-6" data-id="element-14114">
              <fieldset className="border border-gray-200 rounded-md p-4" data-id="element-14115">
                <legend className="text-sm font-medium text-gray-700 px-2" data-id="element-14116">
                  Delivery Address
                </legend>
                <div className="mb-4" data-id="element-14117">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14118">
                    Address Type
                  </label>
                  <div className="flex space-x-4" data-id="element-14119">
                    <div className="flex items-center" data-id="element-14120">
                      <input id="addressTypeTenant" name="addressType" type="radio" value="tenant" checked={formData.deliveryAddress.type === 'tenant'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14121" />
                      <label htmlFor="addressTypeTenant" className="ml-2 block text-sm text-gray-700" data-id="element-14122">
                        Tenant Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-14123">
                      <input id="addressTypeEntity" name="addressType" type="radio" value="entity" checked={formData.deliveryAddress.type === 'entity'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14124" />
                      <label htmlFor="addressTypeEntity" className="ml-2 block text-sm text-gray-700" data-id="element-14125">
                        Entity Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-14126">
                      <input id="addressTypeSite" name="addressType" type="radio" value="site" checked={formData.deliveryAddress.type === 'site'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14127" />
                      <label htmlFor="addressTypeSite" className="ml-2 block text-sm text-gray-700" data-id="element-14128">
                        Site Address
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-14129">
                      <input id="addressTypeCustom" name="addressType" type="radio" value="custom" checked={formData.deliveryAddress.type === 'custom'} onChange={handleAddressTypeChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-14130" />
                      <label htmlFor="addressTypeCustom" className="ml-2 block text-sm text-gray-700" data-id="element-14131">
                        Custom Address
                      </label>
                    </div>
                  </div>
                </div>
                {formData.deliveryAddress.type !== 'custom' && <div className="mb-4" data-id="element-14132">
                    <label htmlFor="addressSelect" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14133">
                      Select Address
                    </label>
                    <select id="addressSelect" value={formData.deliveryAddress.addressId} onChange={handleAddressSelect} className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-14134">
                      <option value="" data-id="element-14135">Select an address</option>
                      {addresses[formData.deliveryAddress.type].map(address => <option key={address.id} value={address.id} data-id="element-14136">
                            {address.name} - {address.street}, {address.city}
                          </option>)}
                    </select>
                    {/* Display selected address details */}
                    {formData.deliveryAddress.addressId && <div className="mt-3 p-3 bg-gray-50 rounded-md" data-id="element-14137">
                        {(() => {
                    const selectedAddress = addresses[formData.deliveryAddress.type].find(addr => addr.id === formData.deliveryAddress.addressId);
                    if (selectedAddress) {
                      return <div className="flex items-start" data-id="element-14138">
                                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-2" data-id="element-14139" />
                                <div data-id="element-14140">
                                  <p className="text-sm font-medium" data-id="element-14141">
                                    {selectedAddress.name}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-14142">
                                    {selectedAddress.street},{' '}
                                    {selectedAddress.city},
                                    {selectedAddress.state && ` ${selectedAddress.state},`}{' '}
                                    {selectedAddress.postalCode}
                                  </p>
                                  <p className="text-sm text-gray-500" data-id="element-14143">
                                    {selectedAddress.country}
                                  </p>
                                </div>
                              </div>;
                    }
                    return null;
                  })()}
                      </div>}
                  </div>}
                {formData.deliveryAddress.type === 'custom' && <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-14144">
                    <div className="sm:col-span-6" data-id="element-14145">
                      <label htmlFor="customAddress_name" className="block text-sm font-medium text-gray-700" data-id="element-14146">
                        Address Name/Label
                      </label>
                      <input type="text" name="customAddress_name" id="customAddress_name" value={formData.deliveryAddress.customAddress.name} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="e.g., Warehouse, Client Site" data-id="element-14147" />
                    </div>
                    <div className="sm:col-span-6" data-id="element-14148">
                      <label htmlFor="customAddress_street" className="block text-sm font-medium text-gray-700" data-id="element-14149">
                        Street Address
                      </label>
                      <input type="text" name="customAddress_street" id="customAddress_street" value={formData.deliveryAddress.customAddress.street} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14150" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14151">
                      <label htmlFor="customAddress_city" className="block text-sm font-medium text-gray-700" data-id="element-14152">
                        City
                      </label>
                      <input type="text" name="customAddress_city" id="customAddress_city" value={formData.deliveryAddress.customAddress.city} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14153" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14154">
                      <label htmlFor="customAddress_state" className="block text-sm font-medium text-gray-700" data-id="element-14155">
                        State / Province
                      </label>
                      <input type="text" name="customAddress_state" id="customAddress_state" value={formData.deliveryAddress.customAddress.state} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14156" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14157">
                      <label htmlFor="customAddress_postalCode" className="block text-sm font-medium text-gray-700" data-id="element-14158">
                        Postal Code
                      </label>
                      <input type="text" name="customAddress_postalCode" id="customAddress_postalCode" value={formData.deliveryAddress.customAddress.postalCode} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14159" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14160">
                      <label htmlFor="customAddress_country" className="block text-sm font-medium text-gray-700" data-id="element-14161">
                        Country
                      </label>
                      <input type="text" name="customAddress_country" id="customAddress_country" value={formData.deliveryAddress.customAddress.country} onChange={handleCustomAddressChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" data-id="element-14162" />
                    </div>
                  </div>}
                {/* Add Delivery Contact after the address selection/entry */}
                <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-14163">
                  <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-14164">
                    Delivery Contact
                  </h4>
                  <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-14165">
                    <div className="sm:col-span-6" data-id="element-14166">
                      <label htmlFor="deliveryContact_name" className="block text-sm font-medium text-gray-700" data-id="element-14167">
                        Contact Name
                      </label>
                      <input type="text" name="deliveryContact_name" id="deliveryContact_name" value={formData.deliveryContact.name} onChange={handleDeliveryContactChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Name of person to contact for delivery" data-id="element-14168" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14169">
                      <label htmlFor="deliveryContact_phone" className="block text-sm font-medium text-gray-700" data-id="element-14170">
                        Phone Number
                      </label>
                      <input type="text" name="deliveryContact_phone" id="deliveryContact_phone" value={formData.deliveryContact.phone} onChange={handleDeliveryContactChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Contact phone number" data-id="element-14171" />
                    </div>
                    <div className="sm:col-span-3" data-id="element-14172">
                      <label htmlFor="deliveryContact_email" className="block text-sm font-medium text-gray-700" data-id="element-14173">
                        Email Address
                      </label>
                      <input type="email" name="deliveryContact_email" id="deliveryContact_email" value={formData.deliveryContact.email} onChange={handleDeliveryContactChange} className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" placeholder="Contact email address" data-id="element-14174" />
                    </div>
                  </div>
                </div>
              </fieldset>
            </div>
          </div>
          <div data-id="element-14175">
            <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-14176">
              Line Items
            </h3>
            <div className="mt-4 space-y-4" data-id="element-14177">
              {formData.items.map((item, index) => <div key={item.id} className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-14178">
                  <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-b border-gray-200" data-id="element-14179">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-14180">
                      Item {index + 1}
                    </h4>
                    <button type="button" onClick={() => removeItem(index)} className="text-red-600 hover:text-red-900" data-id="element-14181">
                      <TrashIcon className="h-5 w-5" data-id="element-14182" />
                    </button>
                  </div>
                  {item.catalogItemId && <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md" data-id="element-14183">
                      <div className="flex items-center" data-id="element-14184">
                        <BookOpenIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-14185" />
                        <span className="text-sm font-medium text-blue-700" data-id="element-14186">
                          Catalog Item: {item.catalogItemId}
                        </span>
                      </div>
                    </div>}
                  <div className="p-4" data-id="element-14187">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6" data-id="element-14188">
                      <div className="sm:col-span-3" data-id="element-14189">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14190">
                          Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.type} onChange={e => handleItemChange(index, 'type', e.target.value)} data-id="element-14191">
                          <option value="Item" data-id="element-14192">Item</option>
                          <option value="Service" data-id="element-14193">Service</option>
                        </select>
                      </div>
                      <div className="sm:col-span-3" data-id="element-14194">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14195">
                          Item Code
                        </label>
                        <div className="flex" data-id="element-14196">
                          <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.itemCode || ''} onChange={e => handleItemChange(index, 'itemCode', e.target.value)} placeholder="Item code" data-id="element-14197" />
                          <button type="button" onClick={() => openCatalogModal(index)} className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14198">
                            Select from Catalog
                          </button>
                        </div>
                      </div>
                      <div className="sm:col-span-6" data-id="element-14199">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14200">
                          Description <span className="text-red-500" data-id="element-14201">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.description ? 'border-red-300' : ''}`} value={item.description} onChange={e => handleItemChange(index, 'description', e.target.value)} placeholder="Brief description" data-id="element-14202" />
                        {errors.lineItems && errors.lineItems[index]?.description && <p className="mt-1 text-sm text-red-600" data-id="element-14203">
                              {errors.lineItems[index].description}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-14204">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14205">
                          Quantity <span className="text-red-500" data-id="element-14206">*</span>
                        </label>
                        <input type="number" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.lineItems && errors.lineItems[index]?.quantity ? 'border-red-300' : ''}`} value={item.quantity} onChange={e => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} min="0" step="1" data-id="element-14207" />
                        {errors.lineItems && errors.lineItems[index]?.quantity && <p className="mt-1 text-sm text-red-600" data-id="element-14208">
                              {errors.lineItems[index].quantity}
                            </p>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-14209">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14210">
                          Unit of Measure
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.uom} onChange={e => handleItemChange(index, 'uom', e.target.value)} data-id="element-14211">
                          {uomOptions.map(uom => <option key={uom} value={uom} data-id="element-14212">
                              {uom}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-14213">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14214">
                          Unit Price <span className="text-red-500" data-id="element-14215">*</span>
                        </label>
                        <input type="number" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} min="0" step="0.01" data-id="element-14216" />
                      </div>
                      <div className="sm:col-span-2" data-id="element-14217">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14218">
                          Tax Code
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.taxCode} onChange={e => handleItemChange(index, 'taxCode', e.target.value)} data-id="element-14219">
                          {taxCodes.map(code => <option key={code.code} value={code.code} data-id="element-14220">
                              {code.code} - {code.name}
                            </option>)}
                        </select>
                      </div>
                      <div className="sm:col-span-2" data-id="element-14221">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14222">
                          Tax Amount
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-sm font-medium" data-id="element-14223">
                          {formatCurrency(item.taxAmount || 0, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                      <div className="sm:col-span-2" data-id="element-14224">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14225">
                          GL Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={item.glCode || ''} onChange={e => handleItemChange(index, 'glCode', e.target.value)} placeholder="GL account code" data-id="element-14226" />
                        {/* Remi suggestion for GL Code */}
                        {getGLCodeSuggestion(item) && !item.glCode && <div className="mt-1 flex items-center p-2 bg-blue-50 border-l-4 border-blue-400 rounded text-sm" data-id="element-14227">
                            <InfoIcon className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" data-id="element-14228" />
                            <div className="flex-grow" data-id="element-14229">
                              <p className="text-blue-700" data-id="element-14230">
                                Remi suggests:{' '}
                                <span className="font-mono font-medium" data-id="element-14231">
                                  {getGLCodeSuggestion(item).code}
                                </span>
                                <span className="ml-1" data-id="element-14232">
                                  ({getGLCodeSuggestion(item).name})
                                </span>
                              </p>
                            </div>
                            <button type="button" onClick={() => applySuggestedGLCode(index, getGLCodeSuggestion(item).code)} className="ml-2 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-xs font-medium" data-id="element-14233">
                              Apply
                            </button>
                          </div>}
                      </div>
                      <div className="sm:col-span-2" data-id="element-14234">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14235">
                          Line Total
                        </label>
                        <div className="p-2 bg-gray-50 rounded-md border border-gray-300 text-lg font-medium" data-id="element-14236">
                          {formatCurrency(item.quantity * item.unitPrice, formData.currency || 'USD', locale)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
              <div className="mt-2" data-id="element-14237">
                <button type="button" onClick={addItem} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14238">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-14239" />
                  Add Line Item
                </button>
              </div>
            </div>
          </div>
          <div data-id="element-14240">
            <StandardizedNotes value={formData.notes} onChange={value => setFormData({
            ...formData,
            notes: value
          })} visibility={formData.noteVisibility} onVisibilityChange={visibility => setFormData({
            ...formData,
            noteVisibility: visibility
          })} visibilityDetails={formData.noteVisibilityDetails} onVisibilityDetailsChange={details => setFormData({
            ...formData,
            noteVisibilityDetails: details
          })} label="Purchase Order Notes" placeholder="Add any notes about this purchase order. Select who can view these notes using the visibility options." data-id="element-14241" />
          </div>
          <div className="border-t border-gray-200 pt-4" data-id="element-14242">
            <div className="flex justify-end" data-id="element-14243">
              <div className="w-64 bg-gray-50 p-4 rounded-md border border-gray-200" data-id="element-14244">
                <div className="flex justify-between items-center text-sm" data-id="element-14245">
                  <span className="text-gray-700" data-id="element-14246">Subtotal:</span>
                  <span className="font-medium" data-id="element-14247">
                    {formatCurrency(calculateSubtotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm mt-2" data-id="element-14248">
                  <span className="text-gray-700" data-id="element-14249">Tax:</span>
                  <span className="font-medium" data-id="element-14250">
                    {formatCurrency(calculateTaxTotal(), formData.currency, locale)}
                  </span>
                </div>
                <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between items-center" data-id="element-14251">
                  <span className="text-gray-900 font-medium" data-id="element-14252">Total:</span>
                  <span className="text-lg font-bold" data-id="element-14253">
                    {formatCurrency(calculateTotal(), formData.currency, locale)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6" data-id="element-14254">
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14255">
            {isConversion ? 'Create Purchase Order' : 'Save Purchase Order'}
          </button>
        </div>
      </div>
      {/* Catalog Item Selection Modal */}
      {showCatalogModal && <div className="fixed z-50 inset-0 overflow-y-auto" data-id="element-14256">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-14257">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={closeCatalogModal} data-id="element-14258">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-14259"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-14260">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-14261">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-14262">
                <div className="sm:flex sm:items-start" data-id="element-14263">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-14264">
                    <div className="flex justify-between items-center mb-4" data-id="element-14265">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14266">
                        Select Item from Catalog
                      </h3>
                      <button type="button" onClick={closeCatalogModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-14267">
                        <XIcon className="h-6 w-6" data-id="element-14268" />
                      </button>
                    </div>
                    <div className="mb-4" data-id="element-14269">
                      <div className="relative rounded-md shadow-sm" data-id="element-14270">
                        <input type="text" value={catalogSearchQuery} onChange={e => setCatalogSearchQuery(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search catalog items..." data-id="element-14271" />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-14272">
                          <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-14273" />
                        </div>
                      </div>
                    </div>
                    <div className="mt-2 max-h-96 overflow-y-auto" data-id="element-14274">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-14275">
                        <thead className="bg-gray-50" data-id="element-14276">
                          <tr data-id="element-14277">
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14278">
                              Item Code
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14279">
                              Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14280">
                              Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14281">
                              UoM
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14282">
                              Price
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14283">
                              Tax
                            </th>
                            <th scope="col" className="relative px-6 py-3" data-id="element-14284">
                              <span className="sr-only" data-id="element-14285">Select</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-14286">
                          {filteredCatalogItems.length > 0 ? filteredCatalogItems.map(catalogItem => <tr key={catalogItem.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleCatalogItemSelect(catalogItem)} data-id="element-14287">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-14288">
                                  {catalogItem.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14289">
                                  <div data-id="element-14290">
                                    <div className="font-medium text-gray-900" data-id="element-14291">
                                      {catalogItem.name}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-14292">
                                      {catalogItem.description}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14293">
                                  {catalogItem.type}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14294">
                                  {catalogItem.uom}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14295">
                                  {formatCurrency(catalogItem.unitPrice, formData.currency, locale)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14296">
                                  {catalogItem.taxCode} ({catalogItem.taxRate}%)
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-14297">
                                  <button type="button" onClick={e => {
                            e.stopPropagation();
                            handleCatalogItemSelect(catalogItem);
                          }} className="text-indigo-600 hover:text-indigo-900" data-id="element-14298">
                                    Select
                                  </button>
                                </td>
                              </tr>) : <tr data-id="element-14299">
                              <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-14300">
                                {formData.vendorId && vendorCatalogs[formData.vendorId] ? 'No matching catalog items found' : 'No catalog items available for this vendor'}
                              </td>
                            </tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-14301">
                <button type="button" onClick={closeCatalogModal} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-14302">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </form>;
}